// @file Composable to determine if we should show reactions for a post.
import { useSurfaceStore } from '@@/pinia/surface'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import type { Id } from '@@/types'
import { computed, type Ref } from 'vue'

/**
 * Composable to determine if reactions should be visible for a post
 *
 * @param postId - The ID of the post to check reactions for
 * @returns boolean indicating if reactions should be shown
 */
export function usePostReactionsVisibility(postId: Ref<Id | null | undefined>) {
  const surfaceStore = useSurfaceStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const reactionsStore = useReactionsStore()

  const xReactions = computed(() => {
    // Check screenshot mode - hide reactions if screenshot mode is on but reactions in screenshots is off
    if (surfaceStore.isScreenshotMode && !surfaceStore.xReactionsInScreenshotMode) {
      return false
    }
    // Check reaction count for post
    const reactionCount =
      postId.value == null ? 0 : (reactionsStore.accumulatedReactionsByWishId[postId.value]?.totalReactionsCount ?? 0)

    const reactionData = reactionsStore.currentReactionData

    // Show reactions if:
    // 1. Wall is reactable, OR has existing reactions (reactionCount > 0)
    // 2. AND reaction data is configured
    return (surfaceSettingsStore.isReactable || reactionCount > 0) && reactionData != null
  })

  return xReactions
}
